import clsx from 'clsx';
import type {ProductWithNodes} from '../../../types';

import useEmblaCarousel from 'embla-carousel-react';
import {motion} from 'framer-motion';
import ProductMedia from '../../media/ProductMedia.client';
import {useEffect, useState} from 'react';
import Autoplay from 'embla-carousel-autoplay';

type Props = {
  storefrontProduct: ProductWithNodes;
};

export default function MobileProductGallery({storefrontProduct}: Props) {
  const media = storefrontProduct?.media?.nodes;
  const [emblaRef, emblaApi] = useEmblaCarousel({loop: true}, [Autoplay()]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setSelectedIndex(emblaApi?.selectedScrollSnap());
    };
    emblaApi.on('select', onSelect);
  }, [emblaApi]);

  const CONTAINER = {
    hidden: {opacity: 0, y: -5},
    show: {
      opacity: 1,
      y: 0,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.1,
        duration: 0.7,
        ease: 'easeOut',
      },
    },
  };

  const ITEM = {
    hidden: {opacity: 0},
    show: {opacity: 1},
  };

  if (!media?.length) {
    return null;
  }

  return (
    <motion.div
      variants={CONTAINER}
      initial="hidden"
      animate="show"
      className={clsx('relative block w-full overflow-hidden md:hidden')}
      tabIndex={-1}
      ref={emblaRef}
    >
      <div className="mx-1 flex gap-1">
        {media.map((item, index) => (
          <motion.div
            variants={ITEM}
            key={index}
            className={clsx('relative flex-[0_0_100%] last:mr-1')}
          >
            <ProductMedia
              className={clsx(
                'aspect-[4/5] w-[100vw] select-none bg-stone-200 object-cover',
              )}
              item={item}
              index={index}
            />
          </motion.div>
        ))}
      </div>

      <div className="mt-3 flex gap-2 px-4">
        {media.map((item, index) => (
          <div
            key={item.id}
            className={clsx(
              'h-[1px] flex-grow',
              'transition-color duration-500 ease-out',
              selectedIndex === index ? 'bg-neutral-900' : 'bg-neutral-300',
            )}
          />
        ))}
      </div>
    </motion.div>
  );
}
